import { useEffect, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

/**
 * Shared fetcher for /api/search/global. Returns the same response shape
 * the sidebar GlobalQuickFind already consumes — trimmed leads/clients/
 * invoices buckets plus the trigram "Did you mean…?" suggestions.
 *
 * The hook is also used by the per-entity list pages (/leads, /clients,
 * /invoices) so they can surface the same chip UX when an in-page search
 * comes up sparse. Reusing the same queryKey shape here means all four
 * call sites share the React Query cache for the same query string —
 * typing a query in /leads, then opening the sidebar quick-find, doesn't
 * trigger a duplicate request.
 */
export type GlobalSearchHits = {
  leads: Array<{ id: string }>;
  clients: Array<{ id: string }>;
  invoices: Array<{ id: string }>;
};

export type GlobalSearchResponse = {
  items: GlobalSearchHits;
  suggestions: string[];
};

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function useGlobalSearchSuggestions(rawQuery: string, delay = 250) {
  const debounced = useDebouncedValue(rawQuery, delay).trim();
  return useQuery<GlobalSearchResponse>({
    queryKey: ["/api/search/global", { q: debounced }],
    queryFn: async () => {
      const res = await fetch(
        `/api/search/global?q=${encodeURIComponent(debounced)}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Global search failed: ${res.status}`);
      return res.json();
    },
    enabled: debounced.length > 0,
    placeholderData: keepPreviousData,
  });
}
